// src/views/AccommodationProvider/ProviderAddProperty.js
import React, { useState } from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderAddProperty = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // Form state
  const [formData, setFormData] = useState({
    number_of_single_rooms: '',
    number_of_double_rooms: '',
    no_of_blocks: '',
    singleroomsqm: '',
    doubleroomsqm: ''
  });

  // Calculate total beds and rooms
  const calculateTotals = () => {
    const singleRooms = parseInt(formData.number_of_single_rooms) || 0;
    const doubleRooms = parseInt(formData.number_of_double_rooms) || 0;
    const totalBeds = singleRooms * 1 + doubleRooms * 2;
    const totalRooms = singleRooms + doubleRooms;
    return { totalBeds, totalRooms };
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Property Room Details - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Property Room Details - ${providerName || 'Your Organization'}`;
    } else {
      return 'Property Room Details';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle empty input
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        [name]: ''
      }));
      return;
    }
    
    // Parse and validate positive integer
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    }
    // If invalid (negative or NaN), don't update the state
  };

  const handleBackToPropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });
    setValidationErrors({});

    // Basic client-side validation
    const errors = {};
    const { totalBeds, totalRooms } = calculateTotals();
    
    if (totalRooms === 0) {
      errors.number_of_single_rooms = 'You must specify at least one single or double room.';
      errors.number_of_double_rooms = 'You must specify at least one single or double room.';
    }

    // Validate number of blocks - required and must be > 0
    if (!formData.no_of_blocks || formData.no_of_blocks === '') {
      errors.no_of_blocks = 'Number of blocks is required.';
    } else {
      const noOfBlocks = parseInt(formData.no_of_blocks);
      if (isNaN(noOfBlocks) || noOfBlocks <= 0) {
        errors.no_of_blocks = 'Number of blocks must be greater than 0.';
      }
    }

    // Validate room sizes if rooms are specified
    const singleRooms = parseInt(formData.number_of_single_rooms) || 0;
    const doubleRooms = parseInt(formData.number_of_double_rooms) || 0;
    
    if (singleRooms > 0 && !formData.singleroomsqm) {
      errors.singleroomsqm = 'Single room size is required when you have single rooms.';
    }

    if (doubleRooms > 0 && !formData.doubleroomsqm) {
      errors.doubleroomsqm = 'Double room size is required when you have double rooms.';
    }

    // Validate minimum room sizes
    if (formData.singleroomsqm && parseInt(formData.singleroomsqm) < 8) {
      errors.singleroomsqm = 'Single room size must be at least 8 square meters.';
    }

    if (formData.doubleroomsqm && parseInt(formData.doubleroomsqm) < 14) {
      errors.doubleroomsqm = 'Double room size must be at least 14 square meters.';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setAlert({
        type: 'danger',
        message: 'Please fill in all required fields correctly.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const requestData = {
        number_of_single_rooms: formData.number_of_single_rooms || 0,
        number_of_double_rooms: formData.number_of_double_rooms || 0,
        no_of_blocks: formData.no_of_blocks || null,
        singleroomsqm: formData.singleroomsqm || null,
        doubleroomsqm: formData.doubleroomsqm || null
      };
      
      console.log('Sending request data:', requestData);
      
      const response = await axiosInstance.post(
        `/api/property/room-details/store/`,
        requestData
      );

      if (response.status === 200) {
        setAlert({
          type: 'success',
          message: response.data.message || 'Room details saved successfully! Proceed to next step.',
          visible: true
        });
        
        // Reset form
        setFormData({
          number_of_single_rooms: '',
          number_of_double_rooms: '',
          no_of_blocks: '',
          singleroomsqm: '',
          doubleroomsqm: ''
        });

        // Navigate to contact step after a delay
        setTimeout(() => {
          navigate('/provider-add-property-contact');
        }, 2000);
      }
    } catch (error) {
      console.error('Error adding property:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data?.validation_errors) {
        const backendValidationErrors = error.response.data.validation_errors;
        
        // Convert backend validation errors to frontend format
        const frontendErrors = {};
        Object.entries(backendValidationErrors).forEach(([field, messages]) => {
          frontendErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        
        setValidationErrors(frontendErrors);
        setAlert({
          type: 'danger',
          message: 'Please correct the errors.',
          visible: true
        });
      } else if (error.response?.data?.error) {
        setAlert({
          type: 'danger',
          message: error.response.data.error,
          visible: true
        });
      } else {
        setAlert({
          type: 'danger',
          message: `Failed to add property. Status: ${error.response?.status}. Please try again.`,
          visible: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary" className="me-2">
                Step 2 of 5
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={() => navigate('/provider-add-property')}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Address
            </Button>
          </div>
        </div>
      </div>

      {/* Form Alert */}
      {alert.visible && alert.type === 'success' && (
        <Row className="mb-4">
          <Col md="12">
            <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Property Room Details Form */}
      <Row className="mb-4">
        <Col md="10" className="mx-auto">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-house-add me-2 text-primary"></i>
                Property Room Details
              </CardTitle>
              
              <form onSubmit={handleSubmit}>
                <h3>Room Configuration</h3>

                {/* Number of Single Rooms and Double Rooms - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.number_of_single_rooms ? 'is-invalid' : ''}`}
                        type="number"
                        id="number_of_single_rooms"
                        name="number_of_single_rooms"
                        placeholder="Number of Single Rooms"
                        value={formData.number_of_single_rooms}
                        onChange={handleInputChange}
                        min="0"
                        step="1"
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="number_of_single_rooms">Number of Single Rooms</label>
                      {validationErrors.number_of_single_rooms && (
                        <div className="invalid-feedback">{validationErrors.number_of_single_rooms}</div>
                      )}
                      <small className="text-muted d-block mt-1">
                        Single rooms (1 bed each). Leave blank if none.
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.number_of_double_rooms ? 'is-invalid' : ''}`}
                        type="number"
                        id="number_of_double_rooms"
                        name="number_of_double_rooms"
                        placeholder="Number of Double Rooms"
                        value={formData.number_of_double_rooms}
                        onChange={handleInputChange}
                        min="0"
                        step="1"
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="number_of_double_rooms">Number of Double Rooms</label>
                      {validationErrors.number_of_double_rooms && (
                        <div className="invalid-feedback">{validationErrors.number_of_double_rooms}</div>
                      )}
                      <small className="text-muted d-block mt-1">
                        Double rooms (2 beds each). Leave blank if none.
                      </small>
                    </div>
                  </div>
                </div>

                {/* Number of Blocks - Full width */}
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.no_of_blocks ? 'is-invalid' : ''}`}
                    type="number"
                    id="no_of_blocks"
                    name="no_of_blocks"
                    placeholder="Number of Blocks/Stand-alone Units"
                    value={formData.no_of_blocks}
                    onChange={handleInputChange}
                    min="1"
                    max="99"
                    step="1"
                    disabled={regStatus !== '2' || isSubmitting}
                  />
                  <label htmlFor="no_of_blocks">Number of Blocks/Stand-alone Units</label>
                  {validationErrors.no_of_blocks && (
                    <div className="invalid-feedback">{validationErrors.no_of_blocks}</div>
                  )}
                  <small className="text-muted d-block mt-1">
                    How many separate buildings or blocks does your property have? (1-99)
                  </small>
                </div>

                {/* Calculated Totals - Readonly Display Fields */}
                {(() => {
                  const { totalBeds, totalRooms } = calculateTotals();
                  if (totalRooms > 0) {
                    return (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              className="form-control bg-light"
                              type="text"
                              id="calculated_total_rooms"
                              value={totalRooms}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            />
                            <label htmlFor="calculated_total_rooms">
                              <i className="bi bi-calculator me-1 text-primary"></i>
                              Total Rooms (Calculated)
                            </label>
                            <small className="text-muted d-block mt-1">
                              Automatically calculated from single + double rooms
                            </small>
                          </div>
                        </div>
                        
                        <div className="col-md-6">
                          <div className="form-floating">
                            <input
                              className="form-control bg-light"
                              type="text"
                              id="calculated_total_beds"
                              value={totalBeds}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            />
                            <label htmlFor="calculated_total_beds">
                              <i className="bi bi-calculator me-1 text-primary"></i>
                              Total Beds (Calculated)
                            </label>
                            <small className="text-muted d-block mt-1">
                              Single rooms (×1) + Double rooms (×2) = {totalBeds} beds
                            </small>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <h3 className="mt-4">Room Sizes</h3>

                {/* Room Sizes - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.singleroomsqm ? 'is-invalid' : ''}`}
                        type="number"
                        id="singleroomsqm"
                        name="singleroomsqm"
                        placeholder="Single Room Size (sq meters)"
                        value={formData.singleroomsqm}
                        onChange={handleInputChange}
                        min="8"
                        step="1"
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="singleroomsqm">
                        Single Room Size (sq meters)
                        {(() => {
                          const singleRooms = parseInt(formData.number_of_single_rooms) || 0;
                          return singleRooms > 0 ? <span className="text-danger"> *</span> : '';
                        })()}
                      </label>
                      {validationErrors.singleroomsqm && (
                        <div className="invalid-feedback">{validationErrors.singleroomsqm}</div>
                      )}
                      <small className="text-muted d-block mt-1">
                        Required if you have single rooms. Minimum 8 sq meters.
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.doubleroomsqm ? 'is-invalid' : ''}`}
                        type="number"
                        id="doubleroomsqm"
                        name="doubleroomsqm"
                        placeholder="Double Room Size (sq meters)"
                        value={formData.doubleroomsqm}
                        onChange={handleInputChange}
                        min="14"
                        step="1"
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="doubleroomsqm">
                        Double Room Size (sq meters)
                        {(() => {
                          const doubleRooms = parseInt(formData.number_of_double_rooms) || 0;
                          return doubleRooms > 0 ? <span className="text-danger"> *</span> : '';
                        })()}
                      </label>
                      {validationErrors.doubleroomsqm && (
                        <div className="invalid-feedback">{validationErrors.doubleroomsqm}</div>
                      )}
                      <small className="text-muted d-block mt-1">
                        Required if you have double rooms. Minimum 14 sq meters.
                      </small>
                    </div>
                  </div>
                </div>

                {/* Navigation buttons */}
                <div className="row mt-4">
                  <div className="col-md-12 text-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={regStatus !== '2' || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          Next
                          <i className="bi bi-arrow-right ms-2"></i>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {alert.type === 'danger' && (
                  <div className="alert alert-danger mt-3">
                    {alert.message}
                  </div>
                )}
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Room Requirements Guidelines
              </CardTitle>
              <Row>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Room Configuration:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Single rooms: 1 bed each</li>
                    <li>Double rooms: 2 beds each</li>
                    <li>Must specify at least one room type</li>
                    <li>Maximum 2 students per room allowed</li>
                    <li>Each student must have adequate space</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Room Size Requirements:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Single rooms: Minimum 8 square meters</li>
                    <li>Double rooms: Minimum 14 square meters</li>
                    <li>Include only livable space area</li>
                    <li>Exclude bathroom/storage areas</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Next Steps:</strong>
                  </div>
                  <div className="small text-muted">
                    After adding room details, you'll need to complete property 
                    information, upload required documents, and schedule an inspection 
                    before your property can go live.
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderAddProperty;